import { chakra, Flex, Heading, useDisclosure, useToast } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { nonNumericSymbols, showErrorToast, showSuccessToast } from "../../../../utils";
import { NumericFormInput } from "../../../../components/Forms";
import { VALIDATION_PATTERNS } from "../../../Decarbonization/helpers";
import { CategoryGrid } from "../../../OrganizationQuestionnaire/components/Categories/components/CategoryGrid";
import {
  multimodalTransportationKeys,
  useAddMultimodalTransportationItems,
  useDeleteMultimodalTransportationItem,
  useGetMultimodalTransportationItems,
  useGetTransportTypes,
  useUpdateMultimodalTransportationItem
} from "../../../../hooks/query";
import { ModalWrapper } from "./index";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { DeleteDialog } from "../../../DataEntryPlanning/components/DeleteDialog";
import { useQueryClient } from "react-query";
import { mapMultimodalTransportationFormData } from "../helpers";
import { WarningDialog } from "../../../../components/WarningDialog";

function ModelDetails({ currentElement, tabKey, callMethod }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "multimodalTransportation.modelDetails"
  });
  const { t: globalTranslation } = useTranslation("translation");

  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const FORM_INPUT_KEY = "multimodalTransportation";
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const {
    isOpen: isDeleteItemDialogOpen,
    onOpen: onDeleteItemDialogOpen,
    onClose: onDeleteItemDialogClose
  } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  const formId = `navForm${tabKey}`;
  const currentModel = currentElement;
  const colHeaders = [
    t(`gridColumns.typeOfTransport`),
    t(`gridColumns.subtypeOfTransport`),
    t(`gridColumns.subtypeTwo`),
    t(`gridColumns.distance`)
  ];
  const attributesOrder = ["typeOfTransport", "subtypeOfTransport", "subtypeTwo", "distance"];
  const {isOpen : isWarningDialogOpen, onOpen:onWarningDialogOpen,  onClose : onWarningDialogClose} = useDisclosure();
  
  useEffect( () => {
    if(currentModel.used) {
      onWarningDialogOpen();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  const modelFormDefaultValues = {
    id: currentModel.id,
    averageWeightOfContainer: currentModel.averageWeightOfContainer
  };

  const {
    register,
    handleSubmit,
    formState,
    reset
  } = useForm({
    defaultValues: modelFormDefaultValues
  });

  useEffect(() => {
    if (formState.errors && Object.keys(formState.errors).length > 0) {
      callMethod({ isFormValid: false });
    }
    if (formState.isDirty !== isDirty) {
      // Workaround that guarantees the actual value of isDirty attribute is used
      setIsDirty(formState.isDirty);
    }
  }, [formState, isDirty, callMethod]);

  const {
    data: transportTypes
  } = useGetTransportTypes({
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    select : (data) => data.filter(item => item.code !== "MULTIMODAL") // remove MULTIMODAL from this screen entirely
  });



  const onSubmit = (data) => {
    const apiData = data;
    apiData.id = currentModel.id;

    if (isDirty) {
      callMethod(apiData);
    } else {
      callMethod(null);
    }
    reset(data);
  };

  const prepareSectionValues = useCallback(items => {
    return transportTypes && transportTypes.length > 0 ?
      items.map(item => {
        const type = transportTypes.find(type => type.id === item.typeOfTransport);
        const subtype = transportTypes.find(type => type.id === item.typeOfTransport)?.subtypes?.find(
          sub => sub.id === item.subtypeOfTransport);
        const subTwo = transportTypes.find(type => type.id === item.typeOfTransport)?.subtypes?.find(
            sub => sub.id === item.subtypeOfTransport)?.subtypeTwos?.find(
            sub2 => sub2.id === item.subtypeTwo);

        return {
          id: item.id ?? null,

          typeOfTransportValue: type?.code ?? "",
          typeOfTransport: type?.name ?? "",

          subtypeOfTransportValue: subtype?.code ?? "",
          subtypeOfTransport: subtype?.name ?? "",

          subtypeTwoValue: item.subtypeTwo !== "" ? subTwo?.code ?? "" : "",
          subtypeTwo: item.subtypeTwo !== "" ? subTwo?.name ?? "" : "",

          distance: item.distance ?? ""
        };
      }) : [];
  }, [transportTypes]);

  const handleAddItem = () => {
    setModalData({
      fieldArrayName: FORM_INPUT_KEY,
      columnHeaders: [...colHeaders, ""],
      modalHeaderItemsType: t("itemsType")
    });
    onModalOpen();
  };

  const handleEditItem = (item) => {
    setModalData({
      fieldArrayName: FORM_INPUT_KEY,
      editData: {
        id: item.id,
        typeOfTransport: item.typeOfTransportValue,
        subtypeOfTransport: item.subtypeOfTransportValue,
        subtypeTwo: item.subtypeTwoValue,
        distance: item.distance
      },
      columnHeaders: colHeaders,
      modalHeaderItemsType: t("itemsType")
    });
    onModalOpen();
  };

  const handleDeleteItem = (item) => {
    onDeleteItemDialogOpen();
    setSelectedItem({ id: item.id });
  };

  const handleDeleteItemDialogClose = () => {
    setSelectedItem(null);
    onDeleteItemDialogClose();
  };

  const deleteRecord = () => {
    dispatch(showAppSpinner());
    deleteItem({ modelId: currentModel.id, recordId: selectedItem.id });
  };

  const handleModalClose = () => {
    setModalData(null);
    onModalClose();
  };

  const { mutate: addItem } = useAddMultimodalTransportationItems({
    onSuccess: () => {
      handleModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.added"));
      queryClient.invalidateQueries(multimodalTransportationKeys.items());
      queryClient.invalidateQueries(multimodalTransportationKeys.transportTypes());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: updateItem } = useUpdateMultimodalTransportationItem({
    onSuccess: () => {
      handleModalClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.updated"));
      queryClient.invalidateQueries(multimodalTransportationKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: deleteItem } = useDeleteMultimodalTransportationItem({
    onSuccess: () => {
      handleDeleteItemDialogClose();
      showSuccessToast(toast, globalTranslation("organizationQuestionnaire.page.deleted"));
      queryClient.invalidateQueries(multimodalTransportationKeys.items());
      queryClient.invalidateQueries(multimodalTransportationKeys.transportTypes());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const handleModalSubmit = (formData) => {
    const apiData = mapMultimodalTransportationFormData(formData[modalData.fieldArrayName], transportTypes);

    dispatch(showAppSpinner());
    if (modalData.editData) {
      updateItem({
        modelId: currentModel.id,
        recordId: apiData[0].id,
        data: apiData[0]
      });
    } else {
      addItem({
        modelId: currentModel.id,
        data: apiData
      });
    }
  };

  const getEmptyRow = () => {
    return {
      typeOfTransport: "",
      subtypeOfTransport: "",
      subTypeTwo: "",
      distance: ""
    };
  };

  const handleWarningConfirmation = () => {
    onWarningDialogClose();
  };

  return (
    <>
      <chakra.form
        id={formId}
        onSubmit={handleSubmit(onSubmit)}
        flexGrow="1"
        overflowY="auto"
        paddingBlockEnd="4"
        paddingInline="2"
        marginInline="-2"
        height="100%"
      >
        <Flex flexDirection="column" gap={4}>
          <Heading size="sm">{t("enterDetails")}</Heading>
          <NumericFormInput
            label={`${t("averageWeightOfContainer")} *`}
            errors={formState.errors}
            {...register("averageWeightOfContainer", {
              required: globalTranslation("entityRequired", { entityName: t("averageWeightOfContainer") }),
              pattern: VALIDATION_PATTERNS.POSITIVE_NUMBER
            })}
            onKeyDown={(e) => nonNumericSymbols.includes(e.key) && e.preventDefault()}
          />
          {transportTypes && transportTypes.length > 0 ?
            <CategoryGrid
              questionnaireId={currentModel.id}
              columnHeaders={colHeaders}
              attributesOrder={attributesOrder}
              prepareSectionValues={prepareSectionValues}
              handleAddItem={handleAddItem}
              handleEditItem={handleEditItem}
              handleDeleteItem={handleDeleteItem}
              getItemsFunction={useGetMultimodalTransportationItems}
              isPreview={false}
            /> : null
          }
          <Heading size="xs" textColor={"red"} hidden={!currentModel.used}> <i>{t("modalIsUsedWarning")}</i></Heading>

        </Flex>
      </chakra.form>
      <DeleteDialog
        isOpen={isDeleteItemDialogOpen}
        onClose={handleDeleteItemDialogClose}
        onDelete={deleteRecord}
        entityName={"Item"}
      />
      <WarningDialog
        isOpen={isWarningDialogOpen}
        onOpen={onWarningDialogOpen}
        onClose={onWarningDialogClose}
        onConfirm={handleWarningConfirmation}
        headerText={t("warning")}
        confirmationText={t("modalIsUsedPopup")}
      />
      {
        isModalOpen && modalData !== null && transportTypes !== null && (
          <ModalWrapper
            tabKey={tabKey}
            modalData={modalData}
            attributesOrder={attributesOrder}
            isOpen={isModalOpen}
            onClose={handleModalClose}
            onSubmit={handleModalSubmit}
            getEmptyRow={getEmptyRow}
            transportTypes={transportTypes}
          />
        )
      }
    </>
  );
}

export default ModelDetails;